import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClinicLocation } from '../../common/model/dto/clinic-location';
import { AdminClinicLocationService } from '../../services/admin/admin-clinic-location.service';
import styled from 'styled-components';
import OvClinicLocationUserList from '../UI/organisms/OvClinicLocationUserList';
import OvNoContent from '../UI/molecules/OvNoContent';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvClinicLocationProviderSearchFilter from '../UI/organisms/OvClinicLocationProviderSearchFilter';
import breakpoints from '../../design-system/breakpoints';
import OvButton from '../UI/atoms/OvButton';
import Variables from '../../design-system/variables';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { AssignProviderToClinicLocationRequest } from '../../common/model/dto/assign-provider-to-clinic-location-request';
import OvAssignProviderToClinicLocationDialog from '../UI/molecules/OvAssignProviderToClinicLocationDialog';
import Colours from '../../design-system/colours';
import { Resource } from '../../common/model/type/resource.enum';
import useAuthorized from '../../hooks/use-authorized';
import usePaginationQueryParams from '../../hooks/use-pagination-query-params';
import { stringify } from 'qs';
import { isEmpty } from 'lodash';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import OvPaginationBar from '../UI/molecules/OvPaginationBar';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import { useAdminClinicLocationMutations } from '../../hooks/use-admin-clinic-location-mutations';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { FilterModel } from '../../common/model/ui/filter.model';
import { OrderBy, SortDirection } from '../../common/types';
import { ClinicLocationUtils } from '../../common/utils/services/clinic-location-utils';

const CLINIC_LOCATION_PROVIDER_LIST_LIMIT = 10;

const FilterableClinicLocationProvidersList: FC<{
  selectedClinicLocation?: ClinicLocation;
  clinicId: string;
}> = ({ selectedClinicLocation, clinicId }) => {
  const { t } = useTranslation();

  const applicationOperations = useAuthorized(Resource.Application);
  const clinicProviderOperations = useAuthorized(Resource.ClinicProvider);

  const clinicProviderCreateAllowed =
    clinicProviderOperations.create || applicationOperations.supervise;
  const clinicProviderUpdateAllowed =
    clinicProviderOperations.update || applicationOperations.supervise;
  const clinicProviderDeleteAllowed =
    clinicProviderOperations.delete || applicationOperations.supervise;

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const { offset, orderBy, orderDir, setOffset, setOrderBy, setOrderDir } =
    usePaginationQueryParams();

  const [shouldHideNotRegisteredUsers, setShouldHideNotRegisteredUsers] =
    useQueryParam(
      'should_hide_not_registered_users',
      withDefault(StringParam, '')
    );

  const [clinicRole, setClinicRole] = useQueryParam(
    'clinic_role',
    withDefault(StringParam, '')
  );

  const query = stringify({
    limit: CLINIC_LOCATION_PROVIDER_LIST_LIMIT,
    order_by: isEmpty(orderBy) ? undefined : orderBy,
    order_dir: isEmpty(orderDir) ? undefined : orderDir,
    offset,
    clinic_role: clinicRole,
    should_hide_not_registered_users: shouldHideNotRegisteredUsers,
  });

  const {
    data: paginatedProviders,
    isPending: isGetProvidersPending,
    isFetching: isGetProvidersFetching,
    error: getProvidersError,
  } = useQuery({
    queryKey: ['admin-clinic-location-providers', query],
    queryFn: () =>
      AdminClinicLocationService.getClinicLocationProvidersByQuery(
        query,
        selectedClinicLocation?.id
      ),
    retry: 3,
    enabled: !!selectedClinicLocation?.id && !!orderBy && !!orderDir,
    placeholderData: keepPreviousData,
  });

  const {
    assignProviderToClinicLocation,
    removeProviderFromClinicLocation,
    approveProviderInvitationForUser,
  } = useAdminClinicLocationMutations(selectedClinicLocation?.id ?? '');

  const providers = paginatedProviders?.docs ?? [];
  const total = paginatedProviders?.total ?? 0;

  useEffect(() => {
    if (!orderBy && !orderDir) {
      setOffset(0);
      setOrderBy('provider_added_at');
      setOrderDir('desc');
    }
  }, [setOrderDir, setOrderBy, setOffset, orderBy, orderDir]);

  const activeSortOption =
    ClinicLocationUtils.getClinicLocationProviderSortingOptions().find(
      (option) => option.value === orderBy
    );

  const goToPage = (_: any, value: number) => {
    setOffset((value - 1) * CLINIC_LOCATION_PROVIDER_LIST_LIMIT);
  };

  const handleOnSave = async (
    request: AssignProviderToClinicLocationRequest
  ) => {
    setIsDialogOpen(false);
    await assignProviderToClinicLocation.mutateAsync(request);
  };

  const onFilterChange = (filter: FilterModel) => {
    switch (filter.fieldName) {
      case 'clinic_role':
        setClinicRole(filter.value);
        break;

      case 'should_hide_not_registered_users':
        setShouldHideNotRegisteredUsers(filter.value);
        break;

      default:
        break;
    }
  };

  const onFilterClear = () => {
    setClinicRole(null);
    setShouldHideNotRegisteredUsers(null);
  };

  const onSortByColumn = (orderBy: OrderBy) => {
    setOrderBy(orderBy.order_by);
    setOrderDir(orderBy.order_dir);
  };

  const isLoading =
    isGetProvidersPending ||
    isGetProvidersFetching ||
    assignProviderToClinicLocation.isPending ||
    removeProviderFromClinicLocation.isPending ||
    approveProviderInvitationForUser.isPending;

  return (
    <Container>
      <CenterPane>
        <UserActions>
          <OvPaginationBar
            total={total}
            limit={CLINIC_LOCATION_PROVIDER_LIST_LIMIT}
            offset={offset}
            onChange={goToPage}
          />

          {clinicProviderCreateAllowed && (
            <ButtonWrapper>
              <StyledOvButton onClick={() => setIsDialogOpen(true)}>
                <AddCircleIcon style={{ marginRight: '5' }} />
                {t('clinicLocations.actions.addNewProvider')}
              </StyledOvButton>
            </ButtonWrapper>
          )}
        </UserActions>

        {getProvidersError && (
          <OvErrorMessage message={getProvidersError?.message} />
        )}

        {providers?.length ? (
          <OvClinicLocationUserList
            users={providers}
            clinicLocationId={selectedClinicLocation?.id}
            clinicId={clinicId}
            deleteAction={removeProviderFromClinicLocation}
            deleteActionCtaLabel={t('clinicLocationDetails.actions.removeUser')}
            showClinicRole={true}
            showClinicLocationProviderCreatedAt={true}
            approveInvitationAction={approveProviderInvitationForUser}
            approveInvitationCtaLabel={t(
              'clinicLocationDetails.actions.acceptInvitation'
            )}
            editable={clinicProviderUpdateAllowed}
            deletable={clinicProviderDeleteAllowed}
            tableHeaders={ClinicLocationUtils.getAdminClinicLocationProviderListTableHeaders()}
            onSort={onSortByColumn}
            orderBy={orderBy}
            orderDir={orderDir as SortDirection}
            currentSortColumn={activeSortOption?.value}
          />
        ) : (
          ''
        )}

        {!providers?.length && !isLoading ? (
          <OvNoContent>
            {t('clinicLocationDetails.emptyList.providers')}
          </OvNoContent>
        ) : null}
        {isLoading && <OvLoadingIndicator position="fixed" />}
      </CenterPane>

      {selectedClinicLocation && (
        <OvClinicLocationProviderSearchFilter
          removeFilters={onFilterClear}
          onHandleFilterValueChange={onFilterChange}
          filterValues={{
            clinicRole,
            shouldHideNotRegisteredUsers,
          }}
        />
      )}

      <OvAssignProviderToClinicLocationDialog
        isOpen={isDialogOpen}
        onCancel={() => setIsDialogOpen(false)}
        onSave={handleOnSave}
        title={t('clinicLocationDialogs.assignProviderTitle')}
      />
    </Container>
  );
};

export default FilterableClinicLocationProvidersList;

const Container = styled.section`
  display: flex;
`;

const CenterPane = styled.div`
  flex-grow: 1;
  margin: 0 auto;
  padding: 0 1rem;
`;

const UserActions = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ButtonWrapper = styled.div`
  flex-grow: 1;
  min-width: 13rem;
  display: flex;
  flex-wrap: 'wrap';
  justify-content: flex-end;
  @media (min-width: ${breakpoints.md}) {
    min-width: auto;
  }
`;

const StyledOvButton = styled(OvButton)`
  && {
    display: flex;
    align-items: center;
    text-transform: none;
    padding: 2px 0.625rem 2px 0.25rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    margin-right: 0.5rem;
    background-color: ${Colours.OV_BASE};

    &:last-child {
      margin-right: 0;
    }
  }
`;
